/**
 * @file StatementOfTruthReview.tsx
 * @brief §12 sign gate for MLR export (doctor review flow).
 *
 * @details
 * `embedded` puts this on the A4 paper, which is white in BOTH themes — so
 * inside it the theme tokens are wrong, not just off-brand: in dark mode
 * `text-muted-foreground` and `text-foreground` resolve to near-white and the
 * whole sign-off went invisible on the page. On the paper it paints the same
 * ink the rest of the sheet uses. (13-09-2026)
 *
 * @author DES
 * @date 02-09-2026
 * @updated 13-09-2026
 */

import { useEffect, useState } from "react";
import { LoaderCircle, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { FormFieldGroup } from "@/components/ui/FormDialog";
import { Input } from "@/components/ui/Input";
import { Badge } from "@/components/ui/Badge";
import { ApiClientError } from "@/shared/api/client";
import { toast } from "@/shared/hooks/useToast";
import { useAuth } from "@/shared/auth/auth-context";
import { useMlrSupplements, useSignMlrStatement } from "../api";

type Props = {
  assessmentId: string;
  /** Sit inside the paper without a second card chrome (08-09-2026). */
  embedded?: boolean;
  /**
   * §12.1 questions still unanswered, in the report's words.
   *
   * ⚠️ The signature is refused server-side when this is non-empty
   * (`reportSigning.service.mjs`); this only stops the doctor finding that out
   * by pressing the button. Three blanks in the part of the report that states
   * what kind of claim it is should not reach a signature.
   */
  unansweredClassification?: string[];
};

/** The sheet's ink — see the note above on why tokens cannot be used here. */
const PAPER_INK = "text-[#1a1a1a]";
const PAPER_MUTED = "text-[#444444]";

export function StatementOfTruthReview({
  assessmentId,
  embedded = false,
  unansweredClassification = [],
}: Props) {
  const { user } = useAuth();
  const supplements = useMlrSupplements(assessmentId);
  const signStatement = useSignMlrStatement(assessmentId);
  const defaultSignerName = user?.firstName
    ? `Dr ${user.firstName}${user.lastName ? ` ${user.lastName}` : ""}`
    : "";
  const [signerName, setSignerName] = useState(defaultSignerName);
  const [attestationAccepted, setAttestationAccepted] = useState(false);

  useEffect(() => {
    if (supplements.data?.statementOfTruthSignerName) {
      setSignerName(supplements.data.statementOfTruthSignerName);
    }
  }, [supplements.data?.statementOfTruthSignerName]);

  const isSigned = supplements.data?.statementOfTruthConfirmed === true;

  const onSign = async () => {
    if (!attestationAccepted) {
      toast({
        title: "Confirmation required",
        description: "Confirm the Statement of Truth before signing.",
      });
      return;
    }
    try {
      await signStatement.mutateAsync({
        signerName: signerName.trim() || defaultSignerName,
        attestationAccepted: true,
      });
      toast({
        title: "Statement of Truth signed",
        description: "You can now export the final MLR PDF.",
      });
    } catch (err) {
      const message =
        err instanceof ApiClientError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Sign failed";
      toast({ title: "Could not sign Statement of Truth", description: message });
    }
  };

  return (
    <div
      className={
        embedded
          ? `space-y-3 pt-2 pb-1 ${PAPER_INK}`
          : "space-y-3 rounded-lg border border-border p-4"
      }
    >
      {embedded ? (
        <p className={`text-[9pt] italic ${PAPER_MUTED}`}>
          {isSigned
            ? "Signed — this report can be exported."
            : "Required before the report can be exported."}
        </p>
      ) : (
        <>
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-semibold">Final sign-off</h4>
            {isSigned ? (
              <Badge variant="secondary">Signed</Badge>
            ) : (
              <Badge variant="outline">Required before PDF export</Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            I believe that the facts stated in this report are true. This report
            complies with CPR Part 35.
          </p>
        </>
      )}
      {isSigned && supplements.data?.statementOfTruthSignedAt ? (
        <p className={embedded ? `text-[10.5pt] ${PAPER_INK}` : "text-sm"}>
          Signed by {supplements.data.statementOfTruthSignerName} on{" "}
          {new Date(supplements.data.statementOfTruthSignedAt).toLocaleString(
            "en-GB",
          )}
        </p>
      ) : (
        <>
          {embedded ? (
            /* On the paper this is a signature block, so it is drawn like one:
               a ruled line to sign on — the same `______` convention §12
               already prints — and a squared-off button. The app's pill input
               and rounded CTA read as a web form dropped onto a court
               document. (13-09-2026) */
            <>
              <div className="mt-4 max-w-[320px]">
                <label
                  htmlFor="review-signerName"
                  className={`block text-[10.5pt] font-bold ${PAPER_INK}`}
                >
                  Your name (as on report)
                </label>
                <input
                  id="review-signerName"
                  value={signerName}
                  onChange={(e) => setSignerName(e.target.value)}
                  placeholder="Dr Jane Smith"
                  className="mt-1 w-full border-0 border-b border-[#1a1a1a] bg-transparent px-0 py-1 text-[10.5pt] text-[#1a1a1a] outline-none placeholder:text-[#9a9a9a] focus:border-b-2"
                  style={{
                    fontFamily: '"Times New Roman", Times, Georgia, serif',
                  }}
                />
              </div>
              <label
                className={`mt-3 flex items-start gap-2 text-[10.5pt] ${PAPER_INK}`}
              >
                <input
                  type="checkbox"
                  className="mt-[3px] h-[13px] w-[13px] accent-[#1a1a1a]"
                  checked={attestationAccepted}
                  onChange={(e) => setAttestationAccepted(e.target.checked)}
                />
                <span>
                  I have reviewed this report and believe the facts stated are
                  true.
                </span>
              </label>
              {/*
                  ⚠️ A squared serif "paper" button until 18-09-2026 — drawn to
                  look like part of the document rather than an app control.
                  The idea was that a rounded brand button reads as a web form
                  dropped onto a court document.

                  It stopped holding once it was the only one. Section 6's
                  import and Section 13's MEDYX fetch both went the same day, so
                  the shared house style had one member left, and a single
                  sharp-cornered serif control on a page of rounded ones reads
                  as a mistake rather than as a convention.
               */}
              {unansweredClassification.length > 0 ? (
                <p className={`mt-3 text-[10pt] italic ${PAPER_MUTED}`}>
                  {unansweredClassification.length === 1
                    ? "12.1 has an unanswered question"
                    : `12.1 has ${unansweredClassification.length} unanswered questions`}
                  {" — answer "}
                  {unansweredClassification.length === 1 ? "it" : "them"} above
                  before signing. They print blank, and they state what kind of
                  claim this is.
                </p>
              ) : null}
              <Button
                type="button"
                className="mt-4 font-sans"
                onClick={() => void onSign()}
                disabled={
                  signStatement.isPending ||
                  !attestationAccepted ||
                  unansweredClassification.length > 0
                }
              >
                {signStatement.isPending ? (
                  <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <ShieldCheck className="mr-2 h-4 w-4" />
                )}
                Sign Statement of Truth
              </Button>
            </>
          ) : (
            <>
              <FormFieldGroup
                label="Your name (as on report)"
                htmlFor="review-signerName"
              >
                <Input
                  capitalizeFirst
                  id="review-signerName"
                  value={signerName}
                  onChange={(e) => setSignerName(e.target.value)}
                  placeholder="e.g. Dr Jane Smith"
                />
              </FormFieldGroup>
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={attestationAccepted}
                  onChange={(e) => setAttestationAccepted(e.target.checked)}
                />
                <span>
                  I have reviewed this report and believe the facts stated are
                  true.
                </span>
              </label>
              <Button
                type="button"
                className="font-sans"
                onClick={() => void onSign()}
                disabled={signStatement.isPending}
              >
                {signStatement.isPending ? (
                  <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <ShieldCheck className="mr-2 h-4 w-4" />
                )}
                Sign Statement of Truth
              </Button>
            </>
          )}
        </>
      )}
    </div>
  );
}
